import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, Divider, Alert, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';

import * as manufacturingApi from '../../../lib/api/manufacturing';

/**
 * 이 워크플로우를 고르면 공정마다 누가 하게 되는지 보여준다.
 *
 * 이름만 보고 고르면 그 안의 공정을 누가 맡는지 알 수 없어서, 공정 마스터를 따로 열어봐야 했다.
 *
 * <b>보여주기만 한다.</b> 여기서 바꾸지는 않는다.
 * 담당자를 바꾸는 것은 공정이 실제로 만들어진 뒤 생산 화면에서 할 수 있고, 그쪽에는
 * 권한 검사와 변경 이력이 이미 붙어 있다. 여기서 또 바꿀 수 있게 하면 그 값을 공정이
 * 생길 때까지 어딘가 들고 있어야 하는데, 그만한 값어치가 아직 없다.
 *
 * 배정 판단은 서버가 한다. 화면에서 다시 계산하면 실제 배정과 어긋난다.
 */
const WorkflowAssignmentDialog = ({ open, workflowUuid, workflowName, workDate, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !workflowUuid) return;
    let alive = true;
    setLoading(true);
    setError(null);
    manufacturingApi.getWorkflowAssignments(workflowUuid, workDate)
      .then((res) => {
        if (!alive) return;
        if (res.data?.success) setRows(res.data.data || []);
        else setError(res.data?.message || '담당자를 불러오지 못했습니다.');
      })
      .catch((err) => {
        if (alive) setError(err?.response?.data?.message || '담당자를 불러오지 못했습니다.');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, workflowUuid, workDate]);

  const unsetCount = rows.filter((r) => r.unset).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon color="primary" />
          <Typography variant="h6">공정별 담당자</Typography>
          {workflowName && (
            <Typography variant="body2" color="text.secondary">— {workflowName}</Typography>
          )}
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          공정 마스터에 정해둔 담당자와 {workDate || '오늘'} 기준 휴가를 반영한 <b>예정</b>입니다.
          실제 배정은 공정이 만들어질 때 다시 정해지므로 그 사이 휴가가 생기면 달라질 수 있습니다.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Alert severity="info">이 워크플로우에 등록된 공정이 없습니다.</Alert>
        ) : (
          <>
            {unsetCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                담당자가 정해지지 않은 공정이 {unsetCount}건 있습니다.
                제조관리 &gt; 공정 마스터에서 담당 팀과 주·부 담당자를 지정해두면
                다음부터 자동으로 채워집니다. 지금은 생산담당자가 대신 배정됩니다.
              </Alert>
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={60} align="center">순서</TableCell>
                  <TableCell>공정</TableCell>
                  <TableCell>담당 팀</TableCell>
                  <TableCell>주 / 부</TableCell>
                  <TableCell>배정 예정</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={r.processUuid || i} hover>
                    <TableCell align="center">{r.processSeq ?? i + 1}</TableCell>
                    <TableCell>{r.processNm || '-'}</TableCell>
                    <TableCell>{r.dptNm || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {r.mainWorkerNm || '-'}
                        {r.subWorkerNm ? ` / ${r.subWorkerNm}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {r.unset ? (
                        <Chip size="small" label="설정 필요" color="warning" variant="outlined" />
                      ) : (
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{r.assignedNm || '-'}</Typography>
                          {/* 주담당자가 아닌 사람이 잡혔으면 왜 그런지 적어준다.
                              이유가 없으면 아무 말도 안 하는 편이 낫다 — 정상인데 설명이 붙으면 오히려 불안해진다. */}
                          {r.reason && (
                            <Typography variant="caption" color="warning.main">{r.reason}</Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              담당자를 바꾸시려면 생산계획이 만들어진 뒤 생산 화면의 공정 상세에서 변경하시면 됩니다.
            </Typography>
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowAssignmentDialog;
